import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  Paper,
  TableContainer
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircleOutline as CleanIcon,
  CleaningServices as BroomIcon,
  Hotel as HotelIcon
} from "@mui/icons-material";
import { auth, db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  updateDoc
} from "firebase/firestore";
import Menu from "../../panel/Menu";

function Roomcleanings() {
  const [propertyId, setPropertyId] = useState(null);
  const [roomsMap, setRoomsMap] = useState({});
  const [cleanRooms, setCleanRooms] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  // 1️⃣ FETCH PROPERTY ID FROM AUTHROLES
  useEffect(() => {
    return auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (snap.exists()) {
        setPropertyId(snap.data().propertyId);
      }
    });
  }, []);

  // 2️⃣ FETCH ROOMINFOS BY PROPERTY ID
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "roominfos"),
      where("propertyId", "==", propertyId)
    );

    return onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setRoomsMap(map);
    });
  }, [propertyId]);

  // 3️⃣ FETCH CLEANING ROOMS BY PROPERTY
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "cleaningrooms"),
      where("status", "==", "Cleaning")
    );

    return onSnapshot(q, (snap) => {
      setCleanRooms(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }))
      );
      setLoading(false);
    });
  }, [propertyId]);

  // 4️⃣ JOIN & FILTER BY PROPERTY
  const filteredRooms = cleanRooms
    .filter((r) => roomsMap[r.roomid]) 
    .filter((r) =>
      r.roomno.toLowerCase().includes(searchText.toLowerCase())
    );

  // UPDATE CLEANINGROOM + ROOMINFO STATUS
  const markAsCleaned = async (cleaningRoom) => {
    try {
      const user = auth.currentUser;

      await updateDoc(doc(db, "cleaningrooms", cleaningRoom.id), {
        status: "Cleaned",
        cleanedby: user?.email || user?.uid || "",
        cleanedtime: new Date().toLocaleString()
      });

      await updateDoc(doc(db, "roominfos", cleaningRoom.roomid), {
        status: "Available"
      });
    } catch (error) {
      console.error("Error updating room status:", error);
    }
  };

  return (
    <>
      <Menu />

      <Box
        sx={{
          ml: "260px",
          p: 2, // Reduced main dashboard container padding
          minHeight: "100vh",
          backgroundColor: "#f8fafc"
        }}
      >
        {/* Compact Header Section */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box 
            sx={{ 
              backgroundColor: "#fff", 
              p: 0.75, // Shrunk icon container padding
              borderRadius: "8px", 
              boxShadow: "0px 2px 6px rgba(0,0,0,0.03)",
              display: "flex",
              alignItems: "center"
            }}
          >
            <BroomIcon sx={{ color: "#6366f1", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1e293b", lineHeight: 1.2 }}>
              Housekeeping Dashboard
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Real-time maintenance updates
            </Typography>
          </Box>
        </Box>

        {/* Compact Core Container Card */}
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: "10px", 
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.05)"
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            
            {/* Compact Action Bar */}
            <Box mb={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <TextField
                size="small"
                placeholder="Search room..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 200, // Shrunk search bar width
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.8rem",
                    height: "32px" // Fixed short height
                  }
                }}
              />
              <Chip 
                label={`${filteredRooms.length} Pending`} 
                variant="outlined" 
                size="small"
                sx={{ fontWeight: 600, color: "#475569", borderColor: "#cbd5e1", height: "24px", fontSize: "0.75rem" }}
              />
            </Box>

            {loading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress color="primary" size={24} thickness={4} />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                <Table size="small"> {/* Enforced small native padding inside rows */}
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, py: 1, fontSize: "0.75rem", width: "40px" }}>#</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, py: 1, fontSize: "0.75rem" }}>Room</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, py: 1, fontSize: "0.75rem" }}>Type</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, py: 1, fontSize: "0.75rem", width: "140px" }}>Status</TableCell>
                      <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600, py: 1, pr: 2, fontSize: "0.75rem", width: "60px" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredRooms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#94a3b8", fontSize: "0.8rem" }}>
                          No active rooms require cleaning.
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredRooms.map((room, index) => {
                      const roomInfo = roomsMap[room.roomid];

                      return (
                        <TableRow 
                          key={room.id} 
                          hover 
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell sx={{ color: "#64748b", py: 1, fontSize: "0.8rem" }}>{index + 1}</TableCell>

                          <TableCell sx={{ py: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HotelIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                              <Typography fontWeight={600} sx={{ color: "#0f172a", fontSize: "0.8rem" }}>
                                {room.roomno}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ color: "#334155", py: 1, fontSize: "0.8rem" }}>
                            {roomInfo?.roomTypeName || "—"}
                          </TableCell>

                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              label="Cleaning"
                              size="small"
                              sx={{
                                backgroundColor: "#fffbeb",
                                color: "#b45309",
                                fontWeight: 600,
                                border: "1px solid #fde68a",
                                fontSize: "0.7rem",
                                height: "20px" // Ultra compact chip line
                              }}
                            />
                          </TableCell>

                          <TableCell align="right" sx={{ py: 1, pr: 1.5 }}>
                            <Tooltip title="Mark Cleaned" arrow placement="top">
                              <IconButton
                                onClick={() => markAsCleaned(room)}
                                size="small" // Mini sizing for the action icon button
                                sx={{
                                  color: "#10b981",
                                  backgroundColor: "#ecfdf5",
                                  p: 1,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#10b981",
                                    color: "#fff",
                                    transform: "scale(1.05)"
                                  }
                                }}
                              >
                                <CleanIcon sx={{ fontSize: 22 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

export default Roomcleanings;